/**
 * useTableColumns — 3-Layer Column Intersection Hook
 *
 * Bridges FormSchema (backend field definitions) + Lens evaluation (visibility
 * filtering) to produce Tabulator-ready column definitions.
 *
 * 3-Layer Model:
 *   Layer 1: FormSchema → all possible columns for this primitive type
 *   Layer 2: Lens → narrows to fields the current actor is allowed to see
 *   Layer 3: User preference → localStorage toggle (handled by TabulatorPrimitiveManager)
 *
 * This hook handles Layers 1 and 2. Layer 3 is applied by the calling component.
 *
 * FERP Principles:
 * - Dumb Frontend: All field definitions from backend FormSchema API
 * - Data-driven: Column list derived from metadata, not hardcoded
 * - Backward compatible: Returns hasSchema=false when no FormSchema exists,
 *   allowing the caller to fall back to hardcoded columns
 */

import { useMemo } from 'react';
import type { PrimitiveType } from '@/config/primitive-metadata';
import { useFormSchema } from '@/components/UniversalEntityForm/useFormSchema';
import {
  formFieldsToTabulatorColumns,
  type ExtendedColumn,
} from '@/utils/formFieldToTabulatorColumn';

// ============================================================================
// TYPES
// ============================================================================

export interface UseTableColumnsOptions {
  /** The primitive type to get columns for */
  primitiveType: PrimitiveType;
  /**
   * Field names visible after lens evaluation (Layer 2).
   * When null/undefined, lens filtering is skipped (all columns pass through).
   * From: useLensEvaluation().evaluation.visibleFields
   */
  lensVisibleFields?: string[] | null;
}

export interface UseTableColumnsResult {
  /** All columns from FormSchema (Layer 1 only) */
  allColumns: ExtendedColumn[];
  /** Columns after lens intersection (Layer 1 ∩ Layer 2) */
  lensFilteredColumns: ExtendedColumn[];
  /**
   * Columns marked `groupable: true` by the backend FormSchema.
   * TabulatorPrimitiveManager merges these with GROUPABLE_SYSTEM_FIELDS
   * to populate the group-by menu — backend-driven, never hardcoded.
   */
  groupableFields: ExtendedColumn[];
  /** Whether the FormSchema query is loading */
  loading: boolean;
  /** Whether a FormSchema exists for this primitive type */
  hasSchema: boolean;
  /** FormSchema query error */
  error: Error | undefined;
  /** Ordered section titles from backend FormSchema (for column menu grouping) */
  sectionOrder: string[];
}

// ============================================================================
// HOOK
// ============================================================================

export function useTableColumns({
  primitiveType,
  lensVisibleFields,
}: UseTableColumnsOptions): UseTableColumnsResult {
  // Map primitive type to backend FormSchema query params
  const entityType =
    primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);

  // Layer 1: Fetch FormSchema from backend
  const { schema, loading, error } = useFormSchema({
    subgraph: 'core',
    entityType,
  });

  // Convert FormSchema fields to Tabulator columns (with section categories)
  const allColumns = useMemo(() => {
    if (!schema?.fields || schema.fields.length === 0) return [];
    return formFieldsToTabulatorColumns(schema.fields, schema.layout?.sections);
  }, [schema]);

  // Extract section order from backend layout for column menu grouping
  const sectionOrder = useMemo(() => {
    if (!schema?.layout?.sections || schema.layout.sections.length === 0) {
      return [];
    }
    const sorted = [...schema.layout.sections].sort((a, b) => {
      const oa = a.order ?? 999;
      const ob = b.order ?? 999;
      return oa - ob;
    });
    return sorted.map(s => s.title);
  }, [schema]);

  // Groupable fields — backend-marked fields available for group-by menu
  const groupableFields = useMemo(
    () => allColumns.filter((col) => col.groupable === true),
    [allColumns],
  );

  // Layer 2: Apply lens field filtering
  const lensFilteredColumns = useMemo(() => {
    // No lens filtering requested — pass all columns through
    if (!lensVisibleFields || lensVisibleFields.length === 0) {
      return allColumns;
    }

    const visibleSet = new Set(lensVisibleFields);

    return allColumns.filter((col) => {
      // Essential columns always pass through lens filtering
      if (col.essential) return true;
      // Column passes if its field name is in the lens visible set
      return visibleSet.has(col.field);
    });
  }, [allColumns, lensVisibleFields]);

  const hasSchema = !loading && schema != null && allColumns.length > 0;

  return {
    allColumns,
    lensFilteredColumns,
    groupableFields,
    loading,
    hasSchema,
    error,
    sectionOrder,
  };
}
